import React, { useEffect, useState } from "react";
import axios from "axios";
import api, { API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Users, Loader2, Tractor, Sprout, Wheat, Landmark, HandCoins, Fingerprint, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";

const SCALE = { small: "#F59E0B", medium: "#00FFFF", large: "#8A2BE2" };
const CROP = { grain: Wheat, cash: Tractor, horticulture: Sprout };
const GRADE = { low: "#00FF7F", medium: "#F59E0B", high: "#EF4444" };
const PRODUCER = ["farmer", "cooperative", "collective_executive", "admin"];

const Barrier = ({ b }) => (
  <div data-testid="barrier">
    <div className="flex items-baseline justify-between"><span className="text-[10px] uppercase tracking-widest text-slate-500">Participation barrier</span><span className="font-mono text-2xl font-bold" style={{ color: GRADE[b.grade] }} data-testid="barrier-index">{b.index}<span className="text-xs text-slate-500">/100 · {b.grade}</span></span></div>
    <div className="grid grid-cols-4 gap-1 mt-1">
      {Object.entries(b.components).map(([k, v]) => (
        <div key={k} title={`${k}: ${v}`}><div className="h-1.5 bg-[#050A15]"><div className="h-full" style={{ width: `${v}%`, background: v > 60 ? "#EF4444" : v > 35 ? "#F59E0B" : "#00FF7F" }} /></div><div className="text-[9px] text-slate-500 mt-0.5">{k} {v}</div></div>
      ))}
    </div>
  </div>
);

const CoopCard = ({ c, coopsById, user, onRequested }) => {
  const [busy, setBusy] = useState(null);
  const Icon = CROP[c.segment.crop_class] || Sprout;
  const canAct = user && PRODUCER.includes(user.role) && (coopsById[c.coop_id]?.owner_id === user.id || ["admin", "collective_executive"].includes(user.role));
  const request = async (l) => {
    setBusy(l.lever);
    try {
      const { data } = await api.post("/commons/linkage/requests", { coop_id: c.coop_id, lever: l.lever, amount_try: l.suggested_try, note: `${l.title} for ${c.name} (barrier ${l.barrier_now} to ${l.barrier_after})` });
      toast.success(data.proposal ? `Request anchored — ${data.amount_try.toLocaleString()} TRY proposal awaits 3 public attestations in the treasury` : "Simplified agreement adopted and anchored");
      onRequested();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  return (
    <div className="qs-panel p-4 space-y-3" data-testid={`linkage-coop-${c.coop_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div><div className="font-display font-semibold flex items-center gap-2"><Icon className="h-4 w-4 text-[#00FF7F]" /> {c.name}</div><div className="text-xs text-slate-500">{c.members} members · {c.products.join(", ")} · {c.segment.region}</div></div>
        <div className="flex gap-1 text-[10px] font-mono uppercase">
          <span className="border px-1.5 py-0.5" style={{ color: SCALE[c.segment.scale], borderColor: SCALE[c.segment.scale] + "66" }} data-testid="segment-scale">{c.segment.scale} scale</span>
          <span className="border border-white/15 px-1.5 py-0.5 text-slate-300" data-testid="segment-crop">{c.segment.crop_class}</span>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Barrier b={c.barrier} />
        <div className="text-[10px] font-mono text-slate-500 grid grid-cols-2 gap-x-3 gap-y-0.5 content-start">
          <span>planned</span><span className="text-slate-300">{c.metrics.planned_kg.toLocaleString()} kg</span>
          <span>staple share</span><span className={c.segment.crop_class === "grain" && c.metrics.staple_share < 0.6 ? "text-[#EF4444]" : "text-slate-300"}>{Math.round(c.metrics.staple_share * 100)}%</span>
          <span>scan coverage</span><span className="text-slate-300">{Math.round(c.metrics.scan_coverage * 100)}%</span>
          <span>working capital</span><span className="text-slate-300">{c.metrics.working_capital_need_try.toLocaleString()} TRY</span>
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Differentiated policy mix</div>
        <div className="space-y-1" data-testid="policy-mix">
          {c.policy_mix.map((m) => (
            <div key={m.pillar} className="text-xs border-l-2 border-[#00FF7F]/50 pl-2"><b className="text-[#00FF7F]">{m.pillar}</b> <span className="text-slate-300">— {m.measure}</span><span className="text-slate-600 font-mono"> · KPI {m.kpi}</span></div>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Barrier-lowering levers</div>
        <div className="space-y-1" data-testid="levers">
          {c.levers.map((l) => (
            <div key={l.lever} className="flex flex-wrap items-center justify-between gap-2 text-xs bg-[#050A15] border border-white/5 px-2 py-1.5" data-testid={`lever-${l.lever}`}>
              <div className="min-w-0"><span className="text-slate-200">{l.title}</span><span className="text-slate-500 font-mono"> · {l.reduces} <ArrowDownRight className="inline h-3 w-3" />{l.barrier_now}→{l.barrier_after}</span><div className="text-[10px] text-slate-500">{l.desc}</div></div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono text-[#F59E0B]">{l.suggested_try ? `${l.suggested_try.toLocaleString()} TRY` : "no cost"}</span>
                {canAct && <button data-testid={`request-${l.lever}`} onClick={() => request(l)} disabled={busy === l.lever} className="flex items-center gap-1 border border-[#F59E0B]/50 text-[#F59E0B] px-2 py-0.5 hover:bg-[#F59E0B]/10">{busy === l.lever ? <Loader2 className="h-3 w-3 animate-spin" /> : <HandCoins className="h-3 w-3" />} request</button>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const LinkagePanel = ({ city, coops }) => {
  const { user } = useAuth();
  const { lang } = useLang();
  const [d, setD] = useState(null);
  const [reqs, setReqs] = useState([]);
  const load = () => {
    axios.get(`${API}/commons/linkage`, { params: { city, lang } }).then((r) => setD(r.data)).catch(() => setD({ error: true }));
    axios.get(`${API}/commons/linkage/requests`, { params: { city } }).then((r) => setReqs(r.data)).catch(() => setReqs([]));
  };
  useEffect(() => { setD(null); load(); }, [city, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#00FF7F]" /></div>;
  if (d.error) return <p className="text-sm text-slate-500">Linkage analysis unavailable.</p>;
  const coopsById = Object.fromEntries(coops.map((c) => [c.id, c]));
  return (
    <div className="space-y-4" data-testid="linkage-panel">
      <div className="qs-grad-border p-5 grid lg:grid-cols-3 gap-5">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#00FFFF]"><Users className="h-4 w-4" /> Farmer linkage · {city}</div>
          <div className="font-mono text-4xl font-bold mt-1" style={{ color: d.avg_barrier_index == null ? "#64748B" : d.avg_barrier_index < 35 ? "#00FF7F" : d.avg_barrier_index < 60 ? "#F59E0B" : "#EF4444" }} data-testid="avg-barrier">{d.avg_barrier_index ?? "—"}<span className="text-sm text-slate-500"> avg barrier</span></div>
          <div className="text-xs text-slate-400 mt-1">{d.region} · water stress {d.traits?.water_stress} · mechanization {d.traits?.mechanization}</div>
          <div className="flex flex-wrap gap-1 mt-3" data-testid="segment-matrix">
            {Object.entries(d.segment_matrix).map(([k, n]) => { const [s, c] = k.split("·"); return <span key={k} className="text-[10px] font-mono border px-1.5 py-0.5" style={{ color: SCALE[s], borderColor: SCALE[s] + "66" }}>{n}× {s} · {c}</span>; })}
            {!Object.keys(d.segment_matrix).length && <span className="text-xs text-slate-600">No cooperatives in this city yet.</span>}
          </div>
        </div>
        <div className="lg:col-span-2 text-sm text-slate-300 leading-relaxed whitespace-pre-line" data-testid="linkage-narrative">{d.narrative}</div>
      </div>
      <div className="grid xl:grid-cols-2 gap-4">
        {d.cooperatives.map((c) => <CoopCard key={c.coop_id} c={c} coopsById={coopsById} user={user} onRequested={load} />)}
      </div>
      {reqs.length > 0 && (
        <div className="qs-panel p-4" data-testid="linkage-requests">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1"><Landmark className="h-3.5 w-3.5" /> Support requests · voted in the treasury tab</div>
          {reqs.map((r) => (
            <div key={r.id} className="border-l-2 border-[#F59E0B]/60 pl-3 py-1 mb-2 text-xs" data-testid={`linkage-request-${r.status}`}>
              <div className="flex flex-wrap justify-between gap-2 font-mono"><span className="text-[#F59E0B]">{r.title} · {r.coop_name}</span><span className={r.status === "executed" || r.status === "adopted" ? "text-[#00FF7F]" : "text-[#F59E0B]"}>{r.amount_try ? `${r.amount_try.toLocaleString()} TRY · ` : ""}{r.status.replace("_", " ")}{r.votes ? ` · net ${r.votes.net}/${r.votes.required_net}` : ""}</span></div>
              <div className="text-slate-400">{r.note} · {r.by} · {new Date(r.created_at).toLocaleDateString()}</div>
              <div className="text-[10px] font-mono text-slate-600 truncate flex items-center gap-1"><Fingerprint className="h-3 w-3" /> {r.anchor}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
